import { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Container } from "../layout/Container";
import StoreCard from "../cards/StoreCard";
import CouponCard from "../cards/CouponCard";
import {
  findCategoryBySlug,
  getSubcategories,
  getAllCategorySlugs,
} from "../../utils/categoryHelpers";
import { cPath } from "../../constants";

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  margin-bottom: 1.5rem;
`;

const A = styled(Link)`
  &:hover {
    color: hsl(${({ theme }) => theme.colors.primary});
  }
`;

const CrumbCurrent = styled.span`
  color: hsl(${({ theme }) => theme.colors.foreground});
  font-weight: 500;
`;

const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid hsl(${({ theme }) => theme.colors.border});
  margin-bottom: 1.5rem;
`;

const TabBtn = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ $active, theme }) =>
    $active
      ? `hsl(${theme.colors.primary})`
      : `hsl(${theme.colors.mutedForeground})`};
  position: relative;
  &:hover {
    color: hsl(${({ theme }) => theme.colors.foreground});
  }
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: hsl(${({ theme }) => theme.colors.primary});
    opacity: ${({ $active }) => ($active ? 1 : 0)};
  }
`;

const Count = styled.span`
  margin-left: 0.375rem;
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ $active, theme }) =>
    $active ? `hsl(${theme.colors.primary})` : `hsl(${theme.colors.muted})`};
  color: ${({ $active, theme }) =>
    $active
      ? `hsl(${theme.colors.primaryForeground})`
      : `hsl(${theme.colors.mutedForeground})`};
`;

const SubTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  margin: 0 0 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SubGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const SubBtn = styled.button`
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? `hsl(${theme.colors.primary})` : `hsl(${theme.colors.border})`};
  overflow: hidden;
  text-align: left;
  padding: 0;
  background: hsl(${({ theme }) => theme.colors.card});
  box-shadow: ${({ $active, theme }) =>
    $active ? `0 0 0 2px hsl(${theme.colors.primary} / 0.2)` : "none"};
  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.cardMd};
    border-color: hsl(${({ theme }) => theme.colors.primary} / 0.3);
  }
`;

const SubImgWrap = styled.div`
  aspect-ratio: 16 / 9;
  position: relative;
  overflow: hidden;
  background: hsl(${({ theme }) => theme.colors.muted});
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
  }
  ${SubBtn}:hover & img {
    transform: scale(1.05);
  }
`;

const SubLabel = styled.div`
  padding: 0.625rem;
  background: hsl(${({ theme }) => theme.colors.card});
`;

const SubName = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $active, theme }) =>
    $active ? `hsl(${theme.colors.primary})` : `hsl(${theme.colors.foreground})`};
`;

const FilterTag = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
`;

const Tag = styled.span`
  font-weight: 600;
  color: hsl(${({ theme }) => theme.colors.primary});
  background: hsl(${({ theme }) => theme.colors.primary} / 0.1);
  padding: 0.25rem 0.75rem;
  border-radius: ${({ theme }) => theme.radius.full};
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
`;

const ClearX = styled.button`
  &:hover {
    color: hsl(${({ theme }) => theme.colors.couponMaroon});
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Empty = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 3rem;
  text-align: center;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

export default function CategoryDetailContent({
  slug,
  category: categoryProp,
  categories: categoriesList = [],
  stores: storesProp,
  coupons: couponsProp,
}) {
  const stores = storesProp ?? [];
  const coupons = couponsProp ?? [];
  const category =
    categoryProp ?? findCategoryBySlug(categoriesList, slug || "");
  const [activeTab, setActiveTab] = useState("stores");
  const [activeSubFilter, setActiveSubFilter] = useState(null);

  if (!category) {
    return (
      <Container style={{ padding: "5rem 1rem", textAlign: "center" }}>
        <h1>Category Not Found</h1>
        <Link to={cPath("categories")}>Browse all categories</Link>
      </Container>
    );
  }

  const subcategories = getSubcategories(categoriesList, slug || "");
  const allSlugs = getAllCategorySlugs(categoriesList, slug || "");
  const filterSlugs = activeSubFilter ? [activeSubFilter] : allSlugs;

  const relatedStores = stores.filter((s) => filterSlugs.includes(s.categorySlug));
  const relatedCoupons = coupons.filter(
    (c) => filterSlugs.includes(c.categorySlug) && c.type === "code"
  );
  const relatedDeals = coupons.filter(
    (c) =>
      filterSlugs.includes(c.categorySlug) &&
      (c.type === "deal" || c.type === "sale")
  );

  const tabs = [
    { key: "stores", label: "Stores", count: relatedStores.length },
    { key: "coupons", label: "Coupons", count: relatedCoupons.length },
    { key: "deals", label: "Deals", count: relatedDeals.length },
  ];

  const toggleSub = (subSlug) => {
    setActiveSubFilter(activeSubFilter === subSlug ? null : subSlug);
  };

  const handleTab = (key) => {
    setActiveTab(key);
    setActiveSubFilter(null);
  };

  return (
    <div style={{ paddingBottom: "2rem" }}>
      <Container>
        <Breadcrumb>
          <A to={cPath("")}>Home</A>
          <span>/</span>
          <A to={cPath("categories")}>Categories</A>
          <span>/</span>
          <CrumbCurrent>{category.name}</CrumbCurrent>
        </Breadcrumb>

        <TabRow>
          {tabs.map((tab) => (
            <TabBtn
              key={tab.key}
              type="button"
              $active={activeTab === tab.key}
              onClick={() => handleTab(tab.key)}
            >
              {tab.label}
              <Count $active={activeTab === tab.key}>{tab.count}</Count>
            </TabBtn>
          ))}
        </TabRow>

        {subcategories.length > 0 && (
          <div style={{ marginBottom: "1.5rem" }}>
            <SubTitle>Filter by Subcategory</SubTitle>
            <SubGrid>
              {subcategories.map((sub) => {
                const isActive = activeSubFilter === sub.slug;
                return (
                  <SubBtn
                    key={sub.slug}
                    type="button"
                    $active={isActive}
                    onClick={() => toggleSub(sub.slug)}
                  >
                    <SubImgWrap>
                      <img src={sub.image} alt={sub.name} loading="lazy" />
                      {isActive && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "hsl(221 67% 44% / 0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              background: "hsl(221 67% 44%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: 12,
                            }}
                          >
                            ✓
                          </div>
                        </div>
                      )}
                    </SubImgWrap>
                    <SubLabel>
                      <SubName $active={isActive}>{sub.name}</SubName>
                    </SubLabel>
                  </SubBtn>
                );
              })}
            </SubGrid>
          </div>
        )}

        {activeSubFilter && (
          <FilterTag>
            <span style={{ color: "hsl(220 10% 46%)" }}>Filtering by:</span>
            <Tag>
              {findCategoryBySlug(categoriesList, activeSubFilter)?.name}
              <ClearX type="button" onClick={() => setActiveSubFilter(null)}>
                ×
              </ClearX>
            </Tag>
          </FilterTag>
        )}

        {activeTab === "stores" &&
          (relatedStores.length > 0 ? (
            <Grid>
              {relatedStores.map((store) => (
                <StoreCard key={store.id} store={store} />
              ))}
            </Grid>
          ) : (
            <Empty>
              <p>No stores in this category yet.</p>
              <Link to={cPath("stores")}>Browse all stores</Link>
            </Empty>
          ))}

        {activeTab === "coupons" &&
          (relatedCoupons.length > 0 ? (
            <List>
              {relatedCoupons.map((coupon) => (
                <CouponCard key={coupon.id} coupon={coupon} />
              ))}
            </List>
          ) : (
            <Empty>
              <p>No coupon codes available in this category yet.</p>
              <Link to={cPath("coupons")}>Browse all coupons</Link>
            </Empty>
          ))}

        {activeTab === "deals" &&
          (relatedDeals.length > 0 ? (
            <List>
              {relatedDeals.map((deal) => (
                <CouponCard key={deal.id} coupon={deal} />
              ))}
            </List>
          ) : (
            <Empty>
              <p>No deals available in this category yet.</p>
              <Link to={cPath("")}>Browse all deals</Link>
            </Empty>
          ))}
      </Container>
    </div>
  );
}
